/**
 * settings/cards/ContactCard.tsx
 * -------------------------------
 * What: Contact/support entry point with mail links or support options.
 * Why: Makes it easy for users to get help from the Settings hub.
 */
import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  Modal,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../../lib/ThemeContext';
import { useAuth } from '../../../../lib/AuthContext';
import { supabase } from '../../../../lib/supabase';

interface Props {
  expanded: boolean;
  onToggle: () => void;
}

export default function ContactCard({ expanded, onToggle }: Props) {
  const { theme, themeType } = useTheme();
  const { user } = useAuth();

  const [contactCategory, setContactCategory] = useState<'Support' | 'Feature Requests' | 'Feedback' | 'Other'>('Support');
  const [contactMessage, setContactMessage] = useState('');
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // Compute device type for email body
  const deviceType = Platform.OS === 'web' ? 'web' : Platform.OS;

  async function handleSendContact() {
    if (!contactMessage.trim()) {
      setContactError('Please enter a message.');
      return;
    }
    setContactLoading(true);
    setContactError('');
    try {
      let browser = '-';
      let languages = '-';
      let screenInfo = '-';
      if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
        browser = navigator.userAgent;
        languages = navigator.languages ? navigator.languages.join(', ') : navigator.language;
        screenInfo = typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '-';
      }

      const textBody = `Category: ${contactCategory}\n\nMessage:\n${contactMessage}\n\nUser Email: ${user?.email}\nUser ID: ${user?.id}\n\nDevice Details:\nDevice Type: ${deviceType}\nBrowser: ${browser}\nLanguages: ${languages}\nScreen: ${screenInfo}`;

      const { error } = await supabase.functions.invoke('send-email', {
        body: {
          to: 'inappsupport@logmyhome.com',
          subject: `LMH App Contact - ${contactCategory}`,
          text: textBody,
          userId: user?.id ?? 'unknown',
        },
      });
      if (error) throw error;
      setContactSuccess(true);
      setContactMessage('');
    } catch (err) {
      setContactError('Failed to send. Please try again later.');
    } finally {
      setContactLoading(false);
    }
  }

  // Neon glow
  const glowStyle = useMemo(() => (
    themeType === 'blueNeon'
      ? Platform.select({
          web: { boxShadow: `0 0 6px 1px ${theme.accent}` } as any,
          default: {
            shadowColor: theme.accent,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.5,
            shadowRadius: 6,
            elevation: 6,
          },
        })
      : {}
  ), [themeType, theme.accent]);

  const cardStyle = [styles.cardBase, { backgroundColor: theme.card, borderColor: theme.border }, glowStyle];

  return (
    <View style={cardStyle}> 
      <TouchableOpacity style={[styles.accordionHeader, expanded && { marginBottom: 16 }]} onPress={onToggle}>
        <Text style={[styles.detailsTitle, { color: theme.text, marginBottom: 0 }]}>Contact the Developers</Text>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={24} color={theme.text} />
      </TouchableOpacity>

      {expanded && (
        <>
          <View style={{ height: 1, backgroundColor: theme.border, width: '100%', marginBottom: 16 }} />

          {/* Category Picker */}
          <View style={styles.preferenceSection}>
            <Text style={[styles.preferenceLabel, { color: theme.text }]}>Category</Text>
            <TouchableOpacity
              style={[styles.selector, { backgroundColor: theme.inputBackground, borderColor: theme.border }]}
              onPress={() => setShowCategoryModal(true)}
            >
              <Text style={[styles.selectorText, { color: theme.text }]}>{contactCategory}</Text>
            </TouchableOpacity>
            <Modal visible={showCategoryModal} transparent animationType="fade" onRequestClose={() => setShowCategoryModal(false)}>
              <View style={[styles.modalOverlay, { backgroundColor: theme.modalOverlay }]}>
                <View style={[styles.modalContent, { backgroundColor: theme.modalBackground }]}> 
                  <Text style={[styles.modalTitle, { color: theme.text }]}>Select Category</Text>
                  {['Support', 'Feature Requests', 'Feedback', 'Other'].map((opt) => (
                    <TouchableOpacity
                      key={opt}
                      style={[styles.modalOption, { backgroundColor: contactCategory === opt ? theme.selected : theme.inputBackground, borderColor: theme.border }]}
                      onPress={() => {
                        setContactCategory(opt as any);
                        setShowCategoryModal(false);
                      }}
                    >
                      <Text style={[{ color: theme.text }, contactCategory === opt && { color: theme.buttonText, fontWeight: 'bold' }]}>{opt}</Text>
                    </TouchableOpacity>
                  ))}
                  <TouchableOpacity style={[styles.modalCancel, { backgroundColor: theme.inputBackground }]} onPress={() => setShowCategoryModal(false)}>
                    <Text style={[styles.modalCancelText, { color: theme.text }]}>Cancel</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </Modal>
          </View>

          {/* Message input */}
          <View style={styles.preferenceSection}>
            <Text style={[styles.preferenceLabel, { color: theme.text }]}>Message</Text>
            <TextInput
              style={[styles.input, { backgroundColor: theme.inputBackground, borderColor: theme.border, color: theme.text, height: 120, textAlignVertical: 'top' }]}
              multiline
              numberOfLines={6}
              placeholder="Type your message here…"
              placeholderTextColor={theme.secondaryText}
              value={contactMessage}
              onChangeText={setContactMessage}
            />
          </View>
          {contactError ? <Text style={[styles.errorText, { color: theme.text }]}>{contactError}</Text> : null}
          {contactSuccess ? <Text style={[styles.successText, { color: theme.success }]}>Message sent successfully!</Text> : null}
          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: theme.accent, opacity: contactLoading ? 0.6 : 1 }]}
            onPress={handleSendContact}
            disabled={contactLoading}
          >
            <Ionicons name="send" size={20} color={theme.buttonText} />
            <Text style={[styles.actionButtonText, { color: theme.buttonText }]}>{contactLoading ? 'Sending…' : 'Send'}</Text>
          </TouchableOpacity>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  cardBase: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  accordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  detailsTitle: {
    fontSize: 17,
    fontWeight: '500',
    fontFamily: 'Inter_500Medium',
    marginBottom: 16,
  },
  preferenceSection: {
    marginBottom: 20,
  },
  preferenceLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  selector: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 14,
    marginBottom: 16,
  },
  selectorText: {
    fontSize: 16,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 14,
    fontSize: 16,
  },
  actionButton: {
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    width: 200,
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    marginTop: 8,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  errorText: {
    fontSize: 14,
    marginTop: 4,
  },
  successText: {
    fontSize: 14,
    marginTop: 4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    padding: 24,
    borderRadius: 12,
    width: '80%',
    maxWidth: 400,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: '500',
    fontFamily: 'Inter_500Medium',
    marginBottom: 12,
  },
  modalOption: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    marginBottom: 8,
    width: '100%',
    alignItems: 'center',
  },
  modalCancel: {
    marginTop: 8,
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 8,
    width: '100%',
    alignItems: 'center',
  },
  modalCancelText: {
    fontSize: 16,
  },
}); 